/**
 * Data & Enrichment Page
 * Make/model dictionaries, body types, auto-fill logic, price benchmarks
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Textarea } from '../../../components/ui/textarea';
import {
  ArrowLeft, Database, Save, Plus, Trash2, Edit,
  Car, Search, Upload, Download, RefreshCw, Tag
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DataEnrichmentPage = () => {
  const [activeTab, setActiveTab] = useState('makes');
  const [selectedVehicleType, setSelectedVehicleType] = useState('cars');
  const [makes, setMakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editDialog, setEditDialog] = useState({ open: false, make: null });
  const [newModels, setNewModels] = useState('');

  useEffect(() => {
    fetchMakes();
  }, [selectedVehicleType]);

  const fetchMakes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/vehicles/data/makes`, {
        params: { vehicle_type: selectedVehicleType }
      });
      setMakes(response.data.makes || defaultMakes[selectedVehicleType] || []);
    } catch (error) {
      console.error('Failed to fetch makes:', error);
      setMakes(defaultMakes[selectedVehicleType] || []);
    } finally {
      setLoading(false);
    }
  };

  // Default make/model data
  const defaultMakes = {
    cars: [
      { make: 'Toyota', models: ['Corolla', 'Camry', 'RAV4', 'Prius', 'Land Cruiser', 'Hilux', 'Yaris', 'Vitz', 'Aqua', 'Premio', 'Allion', 'Axio'], count: 1250 },
      { make: 'Honda', models: ['Civic', 'Accord', 'CR-V', 'Fit', 'Vezel', 'Grace', 'City'], count: 890 },
      { make: 'Nissan', models: ['Sunny', 'X-Trail', 'Note', 'Leaf', 'Juke', 'Qashqai'], count: 654 },
      { make: 'Suzuki', models: ['Swift', 'Alto', 'Wagon R', 'Jimny', 'Vitara', 'Ciaz'], count: 1120 },
      { make: 'Mitsubishi', models: ['Lancer', 'Outlander', 'Pajero', 'Montero', 'ASX'], count: 432 },
      { make: 'BMW', models: ['3 Series', '5 Series', 'X1', 'X3', 'X5', '7 Series'], count: 156 },
      { make: 'Mercedes-Benz', models: ['C-Class', 'E-Class', 'S-Class', 'GLC', 'GLE', 'A-Class'], count: 143 },
      { make: 'Audi', models: ['A3', 'A4', 'A6', 'Q3', 'Q5', 'Q7'], count: 98 }
    ],
    'motorcycles-scooters': [
      { make: 'Honda', models: ['CB150R', 'CBR150R', 'PCX', 'Dio', 'Activa', 'Hornet'], count: 450 },
      { make: 'Yamaha', models: ['FZ', 'R15', 'MT-15', 'Ray', 'Fascino'], count: 380 },
      { make: 'Suzuki', models: ['Gixxer', 'Access', 'Burgman', 'Intruder'], count: 290 },
      { make: 'Bajaj', models: ['Pulsar', 'Dominar', 'Avenger', 'Platina'], count: 520 },
      { make: 'TVS', models: ['Apache', 'Jupiter', 'Ntorq', 'Raider'], count: 340 }
    ],
    'three-wheelers': [
      { make: 'Bajaj', models: ['RE Compact', 'RE 4S', 'Maxima'], count: 890 },
      { make: 'Piaggio', models: ['Ape', 'Ape City', 'Ape Xtra'], count: 234 },
      { make: 'TVS', models: ['King Deluxe', 'King Duramax'], count: 156 }
    ]
  };

  const bodyTypes = [
    { name: 'Sedan', icon: '🚗', vehicles: ['cars'] },
    { name: 'SUV', icon: '🚙', vehicles: ['cars'] },
    { name: 'Hatchback', icon: '🚗', vehicles: ['cars'] },
    { name: 'Coupe', icon: '🏎️', vehicles: ['cars'] },
    { name: 'Wagon', icon: '🚗', vehicles: ['cars'] },
    { name: 'Convertible', icon: '🏎️', vehicles: ['cars'] },
    { name: 'MPV', icon: '🚐', vehicles: ['cars', 'vans'] },
    { name: 'Pickup', icon: '🛻', vehicles: ['cars', 'trucks-lorries'] },
    { name: 'Van', icon: '🚐', vehicles: ['vans'] },
    { name: 'Sport', icon: '🏍️', vehicles: ['motorcycles-scooters'] },
    { name: 'Cruiser', icon: '🏍️', vehicles: ['motorcycles-scooters'] },
    { name: 'Scooter', icon: '🛵', vehicles: ['motorcycles-scooters'] },
    { name: 'Passenger', icon: '🛺', vehicles: ['three-wheelers'] },
    { name: 'Cargo', icon: '🛺', vehicles: ['three-wheelers'] }
  ];

  const priceBenchmarks = [
    { category: 'cars', make: 'Toyota', model: 'Corolla', year: 2020, condition: 'Used', avgPrice: 6500000, minPrice: 5800000, maxPrice: 7500000 },
    { category: 'cars', make: 'Honda', model: 'Civic', year: 2020, condition: 'Used', avgPrice: 7200000, minPrice: 6500000, maxPrice: 8200000 },
    { category: 'cars', make: 'Toyota', model: 'Prius', year: 2018, condition: 'Used', avgPrice: 5500000, minPrice: 4800000, maxPrice: 6200000 },
    { category: 'motorcycles-scooters', make: 'Honda', model: 'CB150R', year: 2022, condition: 'Used', avgPrice: 450000, minPrice: 380000, maxPrice: 520000 },
    { category: 'three-wheelers', make: 'Bajaj', model: 'RE Compact', year: 2021, condition: 'Used', avgPrice: 750000, minPrice: 650000, maxPrice: 850000 }
  ];

  const vehicleTypes = [
    { slug: 'cars', name: 'Cars', icon: '🚗' },
    { slug: 'motorcycles-scooters', name: 'Motorcycles', icon: '🏍️' },
    { slug: 'three-wheelers', name: 'Three-Wheelers', icon: '🛺' },
    { slug: 'vans', name: 'Vans', icon: '🚐' },
    { slug: 'trucks-lorries', name: 'Trucks', icon: '🚚' },
    { slug: 'boats-watercraft', name: 'Boats', icon: '🚤' }
  ];

  const handleSaveMake = async () => {
    if (!editDialog.make) return;
    
    const models = newModels.split(',').map(m => m.trim()).filter(m => m);
    
    try {
      await axios.post(`${API_URL}/api/admin/vehicles/data/makes?admin_id=admin`, {
        vehicle_type: selectedVehicleType,
        make: editDialog.make.make,
        models: [...editDialog.make.models, ...models]
      });
      
      fetchMakes();
      setEditDialog({ open: false, make: null });
      setNewModels('');
    } catch (error) {
      console.error('Failed to save:', error);
      // Update locally for demo
      setMakes(prev => prev.map(m => 
        m.make === editDialog.make.make 
          ? { ...m, models: [...m.models, ...models] }
          : m
      ));
      setEditDialog({ open: false, make: null });
      setNewModels('');
    }
  };

  const filteredMakes = makes.filter(m => 
    m.make.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.models.some(model => model.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/vehicles">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Database className="h-6 w-6 text-blue-600" />
              Data & Enrichment
            </h1>
            <p className="text-gray-500">Make/model dictionaries, body types, price benchmarks</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="makes">
            <Car className="h-4 w-4 mr-2" />
            Makes & Models
          </TabsTrigger>
          <TabsTrigger value="body-types">
            <Tag className="h-4 w-4 mr-2" />
            Body Types
          </TabsTrigger>
          <TabsTrigger value="benchmarks">
            <Database className="h-4 w-4 mr-2" />
            Price Benchmarks
          </TabsTrigger>
        </TabsList>

        {/* Makes & Models Tab */}
        <TabsContent value="makes" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Make & Model Dictionary</CardTitle>
                <div className="flex gap-4">
                  <Link to="/admin/vehicles/data/makes">
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      <Database className="h-4 w-4 mr-2" />
                      Multi-Country Management
                    </Button>
                  </Link>
                  <Select value={selectedVehicleType} onValueChange={setSelectedVehicleType}>
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleTypes.map(vt => (
                        <SelectItem key={vt.slug} value={vt.slug}>
                          {vt.icon} {vt.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search makes/models..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 w-64"
                    />
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                For multi-country make/model management with enable/disable per country, use the{' '}
                <Link to="/admin/vehicles/data/makes" className="text-blue-600 hover:underline">
                  Multi-Country Management
                </Link>{' '}
                page.
              </p>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredMakes.map((makeData) => (
                    <div key={makeData.make} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-lg">{makeData.make}</h3>
                          <Badge variant="outline">{makeData.models.length} models</Badge>
                          <Badge variant="secondary">{makeData.count} listings</Badge>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditDialog({ open: true, make: makeData })}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {makeData.models.map((model) => (
                          <Badge key={model} variant="outline" className="bg-gray-50">
                            {model}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Body Types Tab */}
        <TabsContent value="body-types" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Body Type Configuration</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {bodyTypes.map((type) => (
                  <div key={type.name} className="border rounded-lg p-4 text-center hover:bg-gray-50">
                    <span className="text-3xl">{type.icon}</span>
                    <h4 className="font-medium mt-2">{type.name}</h4>
                    <div className="flex flex-wrap justify-center gap-1 mt-2">
                      {type.vehicles.map(v => (
                        <Badge key={v} variant="outline" className="text-xs">{v}</Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Price Benchmarks Tab */}
        <TabsContent value="benchmarks" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Price Benchmark Rules</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Category</th>
                      <th className="text-left py-3 px-4">Make</th>
                      <th className="text-left py-3 px-4">Model</th>
                      <th className="text-left py-3 px-4">Year</th>
                      <th className="text-right py-3 px-4">Min Price</th>
                      <th className="text-right py-3 px-4">Avg Price</th>
                      <th className="text-right py-3 px-4">Max Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {priceBenchmarks.map((benchmark, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <Badge variant="outline">{benchmark.category}</Badge>
                        </td>
                        <td className="py-3 px-4 font-medium">{benchmark.make}</td>
                        <td className="py-3 px-4">{benchmark.model}</td>
                        <td className="py-3 px-4">{benchmark.year}</td>
                        <td className="py-3 px-4 text-right text-red-600">
                          Rs. {benchmark.minPrice.toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-right font-medium">
                          Rs. {benchmark.avgPrice.toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-right text-green-600">
                          Rs. {benchmark.maxPrice.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  💡 Price benchmarks are automatically updated based on successful transactions and market data.
                  Listings outside the min-max range are flagged for review.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Make Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, make: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {editDialog.make?.make} Models</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label className="mb-2 block">Current Models</Label>
            <div className="flex flex-wrap gap-2 mb-4">
              {editDialog.make?.models.map((model) => (
                <Badge key={model} variant="outline">{model}</Badge>
              ))}
            </div>
            <Label className="mb-2 block">Add New Models (comma-separated)</Label>
            <Textarea
              placeholder="Model 1, Model 2, Model 3..."
              value={newModels}
              onChange={(e) => setNewModels(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, make: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveMake}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataEnrichmentPage;
